import Head from 'next/head'

/**
 * Colors the phone browser's status-bar strip. Hex literals, not tokens: the
 * meta tag cannot read CSS variables. Each one mirrors the token named on it;
 * change them together.
 */
export const THEME_COLORS = {
  /** `bg-edu-bg-primary` → `--color-edu-neutral-11` in the design system's
   *  `tokens/primitives.css`: the app chrome and full-screen overlays. */
  dark: '#1c1c1c',
  /** `bg-surface-white` (`--white` in `pages/tailwind.css`): the enrollment,
   *  register and reset-password pages. */
  white: '#ffffff',
  /** `bg-surface-page` (`--neutral-100` in `pages/tailwind.css`): the Status
   *  Portal. */
  page: '#f6f6f6',
  /** `bg-ja-mint-300` (`pages/tailwind.css`): the email-entry and login pages. */
  mint: '#cde9ca',
  /** The acceptance celebration's full-screen green (`bg-[#0C710B]` in
   *  `AcceptanceCelebration.tsx`). */
  celebrationGreen: '#0c710b',
  /** `bg-black`: the custom celebration's opening beat. */
  black: '#000000',
  /** `bg-edu-brand-blue` → `--color-edu-blue-600` in `tokens/primitives.css`:
   *  the custom celebration's later beats. */
  brandBlue: '#3e51af',
} as const

export type ThemeColorName = keyof typeof THEME_COLORS

/**
 * Overrides the app-wide `theme-color` (set dark in `_app`) for a page whose
 * top edge is not `bg-primary`. Same `key` as the default, so `next/head`
 * keeps only this one.
 *
 * Two mechanisms, because browsers disagree: Android Chrome and Safari before
 * 26 read the `theme-color` meta; Safari 26 ignores it and samples the `body`
 * background instead, which `base.scss` paints `bg-primary` on every page.
 * The style rule recolors the root for that sampler. `html:root` outranks the
 * `html, body` rule in `base.scss` without depending on stylesheet order.
 */
export const ThemeColor = ({ color }: { color: ThemeColorName }) => (
  <Head>
    <meta name='theme-color' content={THEME_COLORS[color]} key='theme-color' />
    <style key='theme-color-root'>
      {`html:root, :root body { background-color: ${THEME_COLORS[color]}; }`}
    </style>
  </Head>
)
